/**
 * 小型键值持久化（M5-03 / DESIGN.md §2.6 附近"OPFS（主）+ IndexedDB（元数据）"）。
 *
 * 快捷键覆盖、"新手引导看过没有"这类**元数据**（几十到几百字节，不是像素/
 * 文件那种大体量数据）——`io/opfs-scratch.ts` 已经示范过"IndexedDB 打不开就
 * 退化到内存实现"这套降级手法（隐私模式下 `indexedDB.open` 会失败），这里
 * 复用同一套模式，但接口更窄：只存/取/删一个 JSON 可序列化的值，不需要
 * `ScratchStore` 那套字节数组/统计信息。
 *
 * 全部方法异步——IndexedDB 本身是异步 API，同步包一层没有意义。
 */

export interface KvStore {
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  /** 全部键值对，调用方自己决定怎么用（快捷键编辑器"导入全部覆盖"要用到）。 */
  entries<T>(): Promise<ReadonlyArray<readonly [string, T]>>;
}

const STORE_NAME = 'kv';

export class IndexedDbKvStore implements KvStore {
  constructor(private readonly db: IDBDatabase) {}

  static async open(dbName: string): Promise<IndexedDbKvStore | null> {
    const factory = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
    if (factory === undefined) return null;
    return new Promise((resolve) => {
      let request: IDBOpenDBRequest;
      try {
        request = factory.open(dbName, 1);
      } catch {
        resolve(null);
        return;
      }
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => resolve(new IndexedDbKvStore(request.result));
      // 隐私模式/配额耗尽下 open 会失败，降级到内存实现（调用方负责）
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
  }

  #tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORE_NAME, mode);
      const request = run(tx.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作失败'));
    });
  }

  async get<T>(key: string): Promise<T | undefined> {
    const value = await this.#tx<unknown>('readonly', (store) => store.get(key) as IDBRequest);
    return value === undefined ? undefined : (value as T);
  }

  async set<T>(key: string, value: T): Promise<void> {
    await this.#tx('readwrite', (store) => store.put(value, key));
  }

  async delete(key: string): Promise<void> {
    await this.#tx('readwrite', (store) => store.delete(key));
  }

  async entries<T>(): Promise<ReadonlyArray<readonly [string, T]>> {
    const keys = await this.#tx<IDBValidKey[]>('readonly', (store) => store.getAllKeys());
    const values = await this.#tx<unknown[]>('readonly', (store) => store.getAll());
    return keys.map((key, i) => [String(key), values[i] as T]);
  }
}

/** 测试/私密模式兜底：不持久化，进程结束（或页面刷新）就丢——比崩溃好，
 * 比假装存住了诚实。 */
export class MemoryKvStore implements KvStore {
  readonly #map = new Map<string, unknown>();

  async get<T>(key: string): Promise<T | undefined> {
    return this.#map.get(key) as T | undefined;
  }

  async set<T>(key: string, value: T): Promise<void> {
    this.#map.set(key, value);
  }

  async delete(key: string): Promise<void> {
    this.#map.delete(key);
  }

  async entries<T>(): Promise<ReadonlyArray<readonly [string, T]>> {
    return [...this.#map.entries()] as Array<readonly [string, T]>;
  }
}

/** 打开一个按名字隔离的 KV 存储，IndexedDB 不可用时透明退化到内存。 */
export async function openKvStore(dbName: string): Promise<KvStore> {
  return (await IndexedDbKvStore.open(dbName)) ?? new MemoryKvStore();
}
